package models

import kotlin.math.roundToInt

/**
 * How dangerous the next roll is, computed from the same rules [BankoGameEngine.applyRoll] uses:
 * nothing busts during the safe opening rolls; after that a 7 busts, Flipside swaps the 7 bust for
 * a doubles bust, and a Danger Zone event adds 6 to the busting totals. Shown next to the pot so a
 * first-time player can see the risk they're riding, and used by the stats sheet.
 */
data class BustRisk(
    /** True while the round is still in its safe opening rolls — nothing can bust yet. */
    val safe: Boolean,
    /** Safe rolls left including the next one (0 once the danger has started). */
    val safeRollsLeft: Int,
    /** Of the 36 equally likely dice outcomes, how many bust the round on the next roll. */
    val bustingOutcomes: Int,
) {
    val bustChance: Double get() = bustingOutcomes / 36.0
    val bustChancePercent: Int get() = (bustChance * 100).roundToInt()

    /** Chance the pot survives the next [rolls] rolls in a row, as a whole percent. */
    fun survivalPercent(rolls: Int): Int {
        var survive = 1.0
        repeat(rolls.coerceAtLeast(0)) { survive *= 1.0 - bustChance }
        return (survive * 100).roundToInt()
    }
}

object BustOdds {
    fun forRound(round: Round): BustRisk {
        if (round.currentRoll <= RoundModifier.SAFE_ROLLS) {
            return BustRisk(
                safe = true,
                safeRollsLeft = RoundModifier.SAFE_ROLLS - round.currentRoll + 1,
                bustingOutcomes = 0,
            )
        }
        val flipside = round.modifier == RoundModifier.FLIPSIDE
        val dangerZone = round.ginkoEvent == GinkoEvent.DANGER_ZONE
        var busting = 0
        for (dieOne in 1..6) {
            for (dieTwo in 1..6) {
                val total = dieOne + dieTwo
                val busts = (total == 7 && !flipside) ||
                    (dangerZone && total == 6) ||
                    (flipside && dieOne == dieTwo)
                if (busts) busting++
            }
        }
        return BustRisk(safe = false, safeRollsLeft = 0, bustingOutcomes = busting)
    }

    /** Probability (0..1) of each dice total 2..12 with two fair dice, indexed from total 2. */
    val expectedTotalShare: List<Double> = ROLL_TOTAL_RANGE.map { total ->
        (6 - kotlin.math.abs(total - 7)) / 36.0
    }
}
